import { Bold, Eye, Heading2, Italic, Link2, List, PencilLine, Quote } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toSafeHtml } from "../lib/content";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export function MarkdownEditor({ value, onChange, placeholder, rows = 18 }: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaId = useId();
  const [previewOpen, setPreviewOpen] = useState(false);

  const commit = (nextValue: string, selectionStart: number, selectionEnd: number) => {
    onChange(nextValue);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const wrapSelection = (before: string, after: string, fallback: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end) || fallback;
    const nextValue = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;
    commit(nextValue, start + before.length, start + before.length + selected.length);
  };

  const prefixLines = (prefix: string, separateFollowingContent = false) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lineStart = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const nextBreak = value.indexOf("\n", end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const lines = value.slice(lineStart, lineEnd).split("\n");
    const shouldRemove = lines.every((line) => line.startsWith(prefix));
    const formatted = lines.map((line) => shouldRemove ? line.slice(prefix.length) : `${prefix}${line}`).join("\n");
    const trailingContent = value.slice(lineEnd);
    const blockSeparator = !shouldRemove && separateFollowingContent && trailingContent.startsWith("\n") && !trailingContent.startsWith("\n\n") ? "\n" : "";
    const nextValue = `${value.slice(0, lineStart)}${formatted}${blockSeparator}${trailingContent}`;
    commit(nextValue, lineStart, lineStart + formatted.length);
  };

  return (
    <div className="markdown-field">
      <div className="markdown-label-row">
        <label htmlFor={textareaId}>Inhalt <small>Markdown-Formatierung</small></label>
        <div className="markdown-label-actions">
          <button type="button" className={previewOpen ? "active" : ""} aria-pressed={previewOpen} onClick={() => setPreviewOpen((current) => !current)}>{previewOpen ? <PencilLine /> : <Eye />}{previewOpen ? "Bearbeiten" : "Vorschau"}</button>
        </div>
      </div>
      <div className={`markdown-editor-shell ${previewOpen ? "markdown-editor-shell--preview" : ""}`}>
        {!previewOpen && <>
          <div className="markdown-toolbar" role="toolbar" aria-label="Text formatieren">
            <button type="button" title="Fett" aria-label="Auswahl fett formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => wrapSelection("**", "**", "fetter Text")}><Bold /><span>Fett</span></button>
            <button type="button" title="Kursiv" aria-label="Auswahl kursiv formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => wrapSelection("_", "_", "kursiver Text")}><Italic /><span>Kursiv</span></button>
            <span className="markdown-toolbar-divider" />
            <button type="button" title="Überschrift" aria-label="Zeile als Überschrift formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => prefixLines("## ")}><Heading2 /><span>Überschrift</span></button>
            <button type="button" title="Liste" aria-label="Auswahl als Liste formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => prefixLines("- ", true)}><List /><span>Liste</span></button>
            <button type="button" title="Zitat" aria-label="Auswahl als Zitat formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => prefixLines("> ", true)}><Quote /><span>Zitat</span></button>
            <button type="button" title="Link" aria-label="Auswahl als Link formatieren" onMouseDown={(event) => event.preventDefault()} onClick={() => wrapSelection("[", "](https://beispiel.de)", "Linktext")}><Link2 /><span>Link</span></button>
          </div>
          <textarea ref={textareaRef} id={textareaId} className="body-editor markdown-textarea" rows={rows} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
        </>}
        {previewOpen && (value.trim()
          ? <div className="markdown-preview article-content" role="region" aria-label="Formatierte Beitragsvorschau" onClick={(event) => event.preventDefault()} dangerouslySetInnerHTML={{ __html: toSafeHtml(value) }} />
          : <div className="markdown-preview-empty" role="status"><Eye /><strong>Noch keine Vorschau</strong><span>Schreibe zuerst einen Beitragstext.</span></div>)}
      </div>
    </div>
  );
}
